import React from "react";
import { NavLink, Route, Switch, useHistory } from "react-router-dom";
import io from "socket.io-client";
import Swal from "sweetalert2";
import {
  MemberChat,
  MemberHome,
  MemberKetentuan,
  MemberLoanForm,
  MemberNotifikasi,
  MemberPelunasan,
  MemberPinjamanDetail,
  MemberPinjamanList,
  MemberProfile,
  MemberSimpanan,
} from "views/Member/MemberPages";
import { clearMemberSession, getMemberIdentity } from "member/memberSession";
import { memberApi } from "member/memberApi";
import { baseUrl } from "stores";
import NotFoundPage from "views/NotFoundPage";

const navItems = [
  { to: "/member/home", label: "Beranda", icon: "fas fa-home" },
  { to: "/member/simpanan", label: "Simpanan", icon: "fas fa-wallet" },
  { to: "/member/pinjaman", label: "Pinjaman", icon: "fas fa-hand-holding-usd" },
  { to: "/member/pelunasan", label: "Pelunasan", icon: "fas fa-receipt" },
  { to: "/member/ketentuan", label: "Ketentuan", icon: "fas fa-info-circle" },
  { to: "/member/chat", label: "Chat", icon: "fas fa-comments" },
  { to: "/member/profile", label: "Profil", icon: "fas fa-user" },
];

export default function MemberLayout() {
  const history = useHistory();
  const identity = getMemberIdentity();
  const [chatUnread, setChatUnread] = React.useState(0);

  const loadChatSummary = React.useCallback(async () => {
    try {
      const response = await memberApi.chatSummary();
      setChatUnread(response.data.data?.unreadForMember || 0);
    } catch (error) {
      setChatUnread(0);
    }
  }, []);

  React.useEffect(() => {
    loadChatSummary();
  }, [loadChatSummary]);

  React.useEffect(() => {
    if (!identity?.idUser) return undefined;

    const socket = io(baseUrl || window.location.origin);
    socket.emit("joinRoom", identity.idUser);
    socket.on("notifikasi", (notification) => {
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "success",
        title: notification,
        showConfirmButton: false,
        timer: 4500,
        timerProgressBar: true,
      });
    });
    socket.on("chat:message", (payload) => {
      if (payload?.message?.idUser !== identity.idUser) return;
      if (payload.message.senderType === "admin") {
        setChatUnread(payload.room?.unreadForMember || 1);
      }
    });
    socket.on("chat:read", (payload) => {
      if (payload?.room?.idUser === identity.idUser) {
        setChatUnread(payload.room.unreadForMember || 0);
      }
    });
    socket.on("chat:deleted", (payload) => {
      if (payload?.idUser === identity.idUser) {
        setChatUnread(0);
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [identity?.idUser]);

  async function logout() {
    const result = await Swal.fire({
      title: "Keluar dari akun?",
      text: "Kamu perlu login ulang untuk mengakses portal member.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Keluar",
      cancelButtonText: "Batal",
      reverseButtons: true,
      focusCancel: true,
      confirmButtonColor: "#6B2FE0",
      cancelButtonColor: "#64748b",
    });

    if (!result.isConfirmed) return;

    clearMemberSession();
    history.replace("/member/auth/login");
  }

  function getNavBadge(item) {
    if (item.to === "/member/chat") return chatUnread;
    return 0;
  }

  return (
    <div className="member-shell">
      <aside className="member-sidebar">
        <div className="member-brand">
          <img src={require("assets/img/pugar.png").default} alt="PUGAR" />
          <div>
            <strong>PUGAR</strong>
            <span>Portal Anggota</span>
          </div>
        </div>
        <nav>
          {navItems.map((item) => {
            const badge = getNavBadge(item);
            return (
              <NavLink key={item.to} to={item.to} activeClassName="active">
                <i className={item.icon} />
                <span>{item.label}</span>
                {badge > 0 && <b className="member-nav-badge">{badge}</b>}
              </NavLink>
            );
          })}
        </nav>
        <button className="member-logout" onClick={logout}>
          <i className="fas fa-sign-out-alt" />
          <span>Keluar</span>
        </button>
      </aside>
      <main className="member-main">
        <header className="member-topbar">
          <div>
            <span>Login sebagai</span>
            <strong>{identity?.nama || "Anggota"}</strong>
          </div>
          <NavLink className="member-icon-button" to="/member/notifikasi">
            <i className="fas fa-bell" />
          </NavLink>
        </header>
        <Switch>
          <Route exact path="/member/home" component={MemberHome} />
          <Route exact path="/member/simpanan" component={MemberSimpanan} />
          <Route exact path="/member/pinjaman" component={MemberPinjamanList} />
          <Route exact path="/member/pinjaman/ajukan" component={MemberLoanForm} />
          <Route exact path="/member/pinjaman/:idPengajuan" component={MemberPinjamanDetail} />
          <Route exact path="/member/pelunasan" component={MemberPelunasan} />
          <Route exact path="/member/ketentuan" component={MemberKetentuan} />
          <Route exact path="/member/chat" component={MemberChat} />
          <Route exact path="/member/notifikasi" component={MemberNotifikasi} />
          <Route exact path="/member/profile" component={MemberProfile} />
          <Route render={() => <NotFoundPage variant="member" />} />
        </Switch>
      </main>
      <nav className="member-bottom-nav">
        {navItems.map((item) => {
          const badge = getNavBadge(item);
          return (
            <NavLink key={item.to} to={item.to} activeClassName="active">
              <i className={item.icon} />
              <span>{item.label}</span>
              {badge > 0 && <b className="member-nav-badge">{badge}</b>}
            </NavLink>
          );
        })}
      </nav>
    </div>
  );
}
